/**
 * Performance metrics form component
 * Allows users to enter spend, impressions, view rates, CTR, CPC, CPM
 * Entry point: Creative detail page only (not during upload)
 */

import * as React from 'react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import type { PerformanceMetrics } from '@/types';

// ============================================================================
// TYPES
// ============================================================================

export interface PerformanceMetricsFormProps {
  initialData?: PerformanceMetrics;
  onSubmit: (data: PerformanceMetrics) => void;
  onCancel?: () => void;
  isLoading?: boolean;
  mediaType?: 'video' | 'image';
}

interface FormData {
  spend: string;
  impressions: string;
  reach: string;
  viewRate2s: string;
  viewRate3s: string;
  thruPlayRate: string;
  ctr: string;
  cpc: string;
  cpm: string;
}

interface FormErrors {
  spend?: string;
  impressions?: string;
  reach?: string;
  viewRate2s?: string;
  viewRate3s?: string;
  thruPlayRate?: string;
  ctr?: string;
  cpc?: string;
  cpm?: string;
}

// ============================================================================
// HELPERS
// ============================================================================

function formatNumber(value: number | undefined): string {
  if (value === undefined || value === null) return '';
  return String(value);
}

function parseNumber(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? undefined : parsed;
}

function parseInteger(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? undefined : parsed;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function PerformanceMetricsForm({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  mediaType = 'video',
}: PerformanceMetricsFormProps) {
  const [formData, setFormData] = React.useState<FormData>({
    spend: formatNumber(initialData?.spend),
    impressions: formatNumber(initialData?.impressions),
    reach: formatNumber(initialData?.reach),
    viewRate2s: formatNumber(initialData?.viewRate2s),
    viewRate3s: formatNumber(initialData?.viewRate3s),
    thruPlayRate: formatNumber(initialData?.thruPlayRate),
    ctr: formatNumber(initialData?.ctr),
    cpc: formatNumber(initialData?.cpc),
    cpm: formatNumber(initialData?.cpm),
  });

  const [errors, setErrors] = React.useState<FormErrors>({});

  // ============================================================================
  // VALIDATION
  // ============================================================================

  const validateField = (field: keyof FormData, value: string): string | undefined => {
    if (!value.trim()) return undefined; // Empty values are allowed

    const num = parseFloat(value);
    if (isNaN(num)) {
      return 'Please enter a valid number';
    }

    // Validate percentage fields (0-100)
    if (['viewRate2s', 'viewRate3s', 'thruPlayRate', 'ctr'].includes(field)) {
      if (num < 0 || num > 100) {
        return 'Value must be between 0 and 100';
      }
    }

    // Validate non-negative fields
    if (num < 0) {
      return 'Value cannot be negative';
    }

    // Validate impressions and reach are integers
    if ((field === 'impressions' || field === 'reach') && !Number.isInteger(num)) {
      return 'Please enter a whole number';
    }

    return undefined;
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    for (const field of Object.keys(formData) as (keyof FormData)[]) {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const metrics: PerformanceMetrics = {
      spend: parseNumber(formData.spend),
      impressions: parseInteger(formData.impressions),
      reach: parseInteger(formData.reach),
      viewRate2s: parseNumber(formData.viewRate2s),
      viewRate3s: parseNumber(formData.viewRate3s),
      thruPlayRate: parseNumber(formData.thruPlayRate),
      ctr: parseNumber(formData.ctr),
      cpc: parseNumber(formData.cpc),
      cpm: parseNumber(formData.cpm),
    };

    // Remove undefined values
    const cleanedMetrics = Object.fromEntries(
      Object.entries(metrics).filter(([, v]) => v !== undefined)
    ) as PerformanceMetrics;

    onSubmit(cleanedMetrics);
  };

  const handleChange = (field: keyof FormData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-sm text-gray-500 mb-4">
        Add performance metrics from your ad platform to see how this creative compares to others in your project.
      </div>

      {/* Cost Metrics */}
      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-gray-900">Cost Metrics</h4>
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Spend ($)"
            type="number"
            step="0.01"
            min="0"
            value={formData.spend}
            onChange={handleChange('spend')}
            error={!!errors.spend}
            errorMessage={errors.spend}
            placeholder="0.00"
            disabled={isLoading}
          />
          <Input
            label="Impressions"
            type="number"
            step="1"
            min="0"
            value={formData.impressions}
            onChange={handleChange('impressions')}
            error={!!errors.impressions}
            errorMessage={errors.impressions}
            placeholder="0"
            disabled={isLoading}
          />
        </div>
      </div>

      {/* View Rate Metrics (video) or Audience (image) */}
      {mediaType === 'video' ? (
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-900">View Rates</h4>
          <div className="grid grid-cols-3 gap-4">
            <Input
              label="2s View Rate (%)"
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={formData.viewRate2s}
              onChange={handleChange('viewRate2s')}
              error={!!errors.viewRate2s}
              errorMessage={errors.viewRate2s}
              placeholder="0.00"
              disabled={isLoading}
            />
            <Input
              label="3s View Rate (%)"
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={formData.viewRate3s}
              onChange={handleChange('viewRate3s')}
              error={!!errors.viewRate3s}
              errorMessage={errors.viewRate3s}
              placeholder="0.00"
              disabled={isLoading}
            />
            <Input
              label="ThruPlay Rate (%)"
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={formData.thruPlayRate}
              onChange={handleChange('thruPlayRate')}
              error={!!errors.thruPlayRate}
              errorMessage={errors.thruPlayRate}
              placeholder="0.00"
              disabled={isLoading}
            />
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-900">Audience</h4>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Reach"
              type="number"
              step="1"
              min="0"
              value={formData.reach}
              onChange={handleChange('reach')}
              error={!!errors.reach}
              errorMessage={errors.reach}
              placeholder="0"
              disabled={isLoading}
            />
          </div>
        </div>
      )}

      {/* Engagement Metrics */}
      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-gray-900">Engagement & Efficiency</h4>
        <div className="grid grid-cols-3 gap-4">
          <Input
            label="CTR (%)"
            type="number"
            step="0.01"
            min="0"
            max="100"
            value={formData.ctr}
            onChange={handleChange('ctr')}
            error={!!errors.ctr}
            errorMessage={errors.ctr}
            placeholder="0.00"
            disabled={isLoading}
          />
          <Input
            label="CPC ($)"
            type="number"
            step="0.01"
            min="0"
            value={formData.cpc}
            onChange={handleChange('cpc')}
            error={!!errors.cpc}
            errorMessage={errors.cpc}
            placeholder="0.00"
            disabled={isLoading}
          />
          <Input
            label="CPM ($)"
            type="number"
            step="0.01"
            min="0"
            value={formData.cpm}
            onChange={handleChange('cpm')}
            error={!!errors.cpm}
            errorMessage={errors.cpm}
            placeholder="0.00"
            disabled={isLoading}
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4">
        {onCancel && (
          <Button
            type="button"
            variant="secondary"
            size="lg"
            className="flex-1"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="flex-1"
          loading={isLoading}
          disabled={isLoading}
        >
          Save Metrics
        </Button>
      </div>
    </form>
  );
}
